/**
 * Merchant Coupon Detail Page
 * Story 4.7: Shows coupon summary and analytics for a specific coupon
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMerchantCouponDetails } from '@/hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { useTranslation } from '../../i18n';
import {
	TimestampService,
	formatForDisplay,
	toPercentage,
	formatMoney,
} from '@zlinkx/shared-v2';
import type { ApiTimestamp } from '@zlinkx/shared-v2';
import { ArrowLeft, Gift, Percent, DollarSign, Tag, Users, TrendingUp, Calendar, Globe, Lock } from 'lucide-react';

const hasText = (value: string | null | undefined): value is string =>
	typeof value === 'string' && value.trim().length > 0;

const CouponDetail: React.FC = () => {
	const { t: rawT } = useTranslation();
	const t = (key: string, defaultValue?: string): string => {
		const options =
			typeof defaultValue === 'string' && defaultValue.length > 0
				? { defaultValue }
				: undefined;
		return rawT(key, options);
	};
	const { couponId: couponIdParam } = useParams<{ couponId: string }>();
	const parsedCouponId =
		typeof couponIdParam === 'string' && couponIdParam.length > 0 ? Number(couponIdParam) : NaN;
	const couponId = Number.isInteger(parsedCouponId) && parsedCouponId > 0 ? parsedCouponId : null;

	const { selectedMerchantId, loading: isLoadingMerchants } = useCurrentMerchant();
	const merchantId = selectedMerchantId;

	// Use the feature hook for coupon details
	const couponDetails = useMerchantCouponDetails({
		merchantId: merchantId ?? 0,
		couponId: couponId ?? 0,
		enabled: merchantId !== null && couponId !== null,
	});

	const coupon = couponDetails.data.coupon;
	const error = couponDetails.error;

	// Combined loading state
	const isLoading = isLoadingMerchants || couponDetails.isLoading;

	// Helper to format discount preview
	const getDiscountPreview = () => {
		if (!coupon) return '';

		if (coupon.coupon_type === 'percentage') {
			return `${coupon.coupon_value}% OFF`;
		} else if (coupon.coupon_type === 'fixed_amount') {
			return `${formatMoney(coupon.coupon_value ?? 0)} OFF`;
		} else if (coupon.coupon_type === 'free_item') {
			return t('merchant.couponDetail.freeItem', 'Free Item');
		} else if (coupon.coupon_type === 'bogo') {
			return t('merchant.couponDetail.bogo', 'Buy One Get One');
		}
		return '';
	};

	// Helper to get coupon type icon
	const getCouponTypeIcon = () => {
		if (!coupon) return <Tag className='w-5 h-5' />;

		switch (coupon.coupon_type) {
			case 'percentage':
				return <Percent className='w-5 h-5' />;
			case 'fixed_amount':
				return <DollarSign className='w-5 h-5' />;
			case 'free_item':
			case 'bogo':
				return <Gift className='w-5 h-5' />;
			default:
				return <Tag className='w-5 h-5' />;
		}
	};

	// Calculate rates with division by zero protection
	const calculateRate = (numerator: number, denominator: number): string => {
		if (denominator === 0) return t('common.notAvailable', 'N/A');
		return `${((numerator / denominator) * 100).toFixed(1)}%`;
	};

	// Get languages list
	const getLanguagesList = (): string[] => {
		if (!coupon) return [];
		const languages = [t('merchant.couponDetail.languages.english', 'English')];
		if (hasText(coupon.title_fr)) languages.push(t('merchant.couponDetail.languages.french', 'French'));
		if (hasText(coupon.title_zh)) languages.push(t('merchant.couponDetail.languages.chinese', 'Chinese'));
		return languages;
	};

	// Format date helper
	const parseCouponDate = (dateValue: ApiTimestamp | Date | number | null | undefined): Date | null => {
		if (dateValue === null || dateValue === undefined) return null;
		if (dateValue instanceof Date) {
			return TimestampService.isValidDate(dateValue) ? dateValue : null;
		}

		if (typeof dateValue === 'number') {
			const ms = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
			const parsed = TimestampService.getCurrentDate();
			parsed.setTime(ms);
			return TimestampService.isValidDate(parsed) ? parsed : null;
		}

		const parsed = TimestampService.parseDateTimeInput(dateValue);
		return TimestampService.isValidDate(parsed) ? parsed : null;
	};

	const formatDate = (dateValue: ApiTimestamp | Date | number | null | undefined): string => {
		if (dateValue === null || dateValue === undefined) return t('common.notSet', 'Not set');
		const date = parseCouponDate(dateValue);
		if (!date) return t('common.invalidDate', 'Invalid date');
		return formatForDisplay(date, { style: 'long' });
	};

	// No couponId provided
	if (couponId === null) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='max-w-4xl mx-auto text-center py-12'>
					<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
						<Tag className='w-8 h-8 text-red-600' />
					</div>
					<h3 className='text-lg font-medium text-gray-900 mb-2'>
						{t('merchant.couponDetail.invalidCoupon', 'Invalid Coupon')}
					</h3>
					<p className='text-gray-600 mb-4'>
						{t('merchant.couponDetail.noCouponId', 'No coupon ID provided.')}
					</p>
					<Link
						to='/merchant/coupon-list'
						className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
					>
						<ArrowLeft className='w-4 h-4 mr-2' />
						{t('merchant.couponDetail.backToCoupons', 'Back to Coupons')}
					</Link>
				</div>
			</div>
		);
	}

	// No merchant available
	if (!isLoadingMerchants && merchantId === null) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='max-w-4xl mx-auto text-center py-12'>
					<div className='w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4'>
						<Tag className='w-8 h-8 text-yellow-600' />
					</div>
					<h3 className='text-lg font-medium text-gray-900 mb-2'>
						{t('merchant.couponDetail.noMerchant', 'No Merchant Account')}
					</h3>
					<p className='text-gray-600 mb-4'>
						{t('merchant.couponDetail.noMerchantMessage', 'You do not have access to any merchant accounts.')}
					</p>
					<Link
						to='/merchant/coupon-list'
						className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
					>
						<ArrowLeft className='w-4 h-4 mr-2' />
						{t('merchant.couponDetail.backToCoupons', 'Back to Coupons')}
					</Link>
				</div>
			</div>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='max-w-4xl mx-auto'>
					{/* Back link skeleton */}
					<div className='mb-6'>
						<div className='h-6 w-32 bg-gray-200 rounded animate-pulse' />
					</div>

					{/* Summary section skeleton */}
					<div className='bg-white rounded-lg shadow-sm border p-6 mb-6'>
						<div className='flex gap-6'>
							<div className='w-48 h-32 bg-gray-200 rounded-lg animate-pulse' />
							<div className='flex-1 space-y-3'>
								<div className='h-8 w-64 bg-gray-200 rounded animate-pulse' />
								<div className='h-4 w-full bg-gray-200 rounded animate-pulse' />
								<div className='h-4 w-3/4 bg-gray-200 rounded animate-pulse' />
							</div>
						</div>
					</div>

					{/* Analytics section skeleton */}
					<div className='bg-white rounded-lg shadow-sm border p-6'>
						<div className='h-6 w-32 bg-gray-200 rounded animate-pulse mb-4' />
						<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
							{[1, 2, 3, 4].map(i => (
								<div key={i} className='p-4 bg-gray-50 rounded-lg'>
									<div className='h-4 w-20 bg-gray-200 rounded animate-pulse mb-2' />
									<div className='h-8 w-16 bg-gray-200 rounded animate-pulse' />
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Error state
	const hasError = hasText(error);
	if (hasError || coupon === null) {
		const isNotFound = hasError && (error.includes('404') || error.toLowerCase().includes('not found'));
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='max-w-4xl mx-auto text-center py-12'>
					<div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
						<Tag className='w-8 h-8 text-gray-400' />
					</div>
					<h3 className='text-lg font-medium text-gray-900 mb-2'>
						{isNotFound
							? t('merchant.couponDetail.notFound', 'Coupon Not Found')
							: t('merchant.couponDetail.error', 'Error Loading Coupon')}
					</h3>
					<p className='text-gray-600 mb-4'>
						{isNotFound
							? t('merchant.couponDetail.notFoundMessage', "The coupon you're looking for doesn't exist or has been deleted.")
							: t('merchant.couponDetail.errorMessage', 'An error occurred while loading the coupon details.')}
					</p>
					<Link
						to='/merchant/coupon-list'
						className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
					>
						<ArrowLeft className='w-4 h-4 mr-2' />
						{t('merchant.couponDetail.backToCoupons', 'Back to Coupons')}
					</Link>
				</div>
			</div>
		);
	}

	const analytics = coupon.analytics;
	const languages = getLanguagesList();
	const hasBackgroundImage = hasText(coupon.background_image_url);
	const hasExpiration = hasText(coupon.expires_at);
	const hasDistributionLock = hasText(coupon.distributed_at);
	const hasMinimumAmount =
		typeof coupon.minimum_amount === 'number' && Number.isFinite(coupon.minimum_amount) && coupon.minimum_amount > 0;
	const minimumAmount = coupon.minimum_amount ?? 0;

	return (
		<div className='flex-1 overflow-auto p-6'>
			<div className='max-w-4xl mx-auto'>
				{/* Back Navigation */}
				<div className='mb-6'>
					<Link
						to='/merchant/coupon-list'
						className='inline-flex items-center text-gray-600 hover:text-gray-900 font-medium'
					>
						<ArrowLeft className='w-4 h-4 mr-2' />
						{t('merchant.couponDetail.backToCoupons', 'Back to Coupons')}
					</Link>
				</div>

				{/* Coupon Summary Section */}
				<div className='bg-white rounded-lg shadow-sm border p-6 mb-6'>
					<div className='flex flex-col md:flex-row gap-6'>
						{/* Coupon Preview Card */}
						<div
							className='w-full md:w-64 h-40 rounded-lg flex items-center justify-center relative overflow-hidden'
							style={{
								backgroundColor: hasBackgroundImage ? 'transparent' : '#3B82F6',
								backgroundImage: hasBackgroundImage ? `url(${coupon.background_image_url})` : undefined,
								backgroundSize: 'cover',
								backgroundPosition: 'center',
							}}
						>
							{/* Overlay for better text visibility */}
							{hasBackgroundImage && (
								<div className='absolute inset-0 bg-black/30' />
							)}

							<div className='relative z-10 text-center text-white p-4'>
								<div className='text-2xl font-bold mb-1'>{getDiscountPreview()}</div>
								<div className='text-sm opacity-90'>{coupon.title}</div>
								{hasExpiration && (
									<div className='text-xs mt-2 opacity-75'>
										{t('merchant.couponDetail.expires', 'Expires:')} {formatDate(coupon.expires_at)}
									</div>
								)}
							</div>
						</div>

						{/* Coupon Details */}
						<div className='flex-1'>
							<div className='flex items-start justify-between mb-4'>
								<div>
									<h1 className='text-2xl font-bold text-gray-900'>{coupon.title}</h1>
									<p className='text-gray-600 mt-1'>{coupon.description}</p>
								</div>
								<div className='flex items-center gap-2'>
									{hasDistributionLock && (
										<span className='px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 flex items-center gap-1'>
											<Lock className='w-3 h-3' />
											{t('merchant.couponDetail.locked', 'Locked')}
										</span>
									)}
									<span
										className={`px-3 py-1 rounded-full text-sm font-medium ${
											coupon.is_active
												? 'bg-green-100 text-green-800'
												: 'bg-red-100 text-red-800'
										}`}
									>
										{coupon.is_active
											? t('merchant.couponDetail.active', 'Active')
											: t('merchant.couponDetail.expired', 'Expired')}
									</span>
								</div>
							</div>

							{/* Details Grid */}
							<div className='grid grid-cols-2 gap-4 text-sm'>
								<div>
									<span className='text-gray-500'>{t('merchant.couponDetail.type', 'Type:')}</span>
									<div className='flex items-center gap-1 font-medium text-gray-900 mt-1'>
										{getCouponTypeIcon()}
										<span className='capitalize'>{coupon.coupon_type.replace('_', ' ')}</span>
									</div>
								</div>
								<div>
									<span className='text-gray-500'>{t('merchant.couponDetail.value', 'Value:')}</span>
									<p className='font-medium text-gray-900 mt-1'>{getDiscountPreview()}</p>
								</div>
								{hasMinimumAmount && (
									<div>
										<span className='text-gray-500'>
											{t('merchant.couponDetail.minimumPurchase', 'Minimum Purchase:')}
										</span>
										<p className='font-medium text-gray-900 mt-1'>
											{formatMoney(minimumAmount)}
										</p>
									</div>
								)}
								<div>
									<span className='text-gray-500'>
										{t('merchant.couponDetail.commission', 'Commission:')}
									</span>
									<p className='font-medium text-gray-900 mt-1'>
										{toPercentage(coupon.commission_basis_points)}%
									</p>
								</div>
								<div>
									<span className='text-gray-500'>
										{t('merchant.couponDetail.expiration', 'Expiration:')}
									</span>
									<p className='font-medium text-gray-900 mt-1 flex items-center gap-1'>
										<Calendar className='w-4 h-4' />
										{formatDate(coupon.expires_at)}
									</p>
								</div>
								<div>
									<span className='text-gray-500'>
										{t('merchant.couponDetail.languagesLabel', 'Languages:')}
									</span>
									<p className='font-medium text-gray-900 mt-1 flex items-center gap-1'>
										<Globe className='w-4 h-4' />
										{languages.join(', ')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Analytics Section */}
				<div className='bg-white rounded-lg shadow-sm border p-6 mb-6'>
					<h2 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
						<TrendingUp className='w-5 h-5' />
						{t('merchant.couponDetail.analytics', 'Analytics')}
					</h2>

					{/* Distribution Metrics */}
					<div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
						<div className='bg-blue-50 rounded-lg p-4'>
							<p className='text-sm text-blue-600 font-medium'>
								{t('merchant.couponDetail.totalDistributed', 'Distributed')}
							</p>
							<p className='text-2xl font-bold text-blue-900'>{analytics.total_distributed}</p>
						</div>
						<div className='bg-purple-50 rounded-lg p-4'>
							<p className='text-sm text-purple-600 font-medium'>
								{t('merchant.couponDetail.totalViews', 'Views')}
							</p>
							<p className='text-2xl font-bold text-purple-900'>{analytics.total_views}</p>
						</div>
						<div className='bg-green-50 rounded-lg p-4'>
							<p className='text-sm text-green-600 font-medium'>
								{t('merchant.couponDetail.totalRedeemed', 'Redeemed')}
							</p>
							<p className='text-2xl font-bold text-green-900'>{analytics.total_redeemed}</p>
						</div>
						<div className='bg-gray-50 rounded-lg p-4'>
							<p className='text-sm text-gray-600 font-medium'>
								{t('merchant.couponDetail.totalExpired', 'Expired')}
							</p>
							<p className='text-2xl font-bold text-gray-900'>{analytics.total_expired}</p>
						</div>
					</div>

					{/* Performance Rates */}
					<div className='grid grid-cols-2 gap-4 mb-6'>
						<div className='border rounded-lg p-4'>
							<p className='text-sm text-gray-500'>
								{t('merchant.couponDetail.redemptionRate', 'Redemption Rate')}
							</p>
							<p className='text-xl font-bold text-gray-900'>
								{calculateRate(analytics.total_redeemed, analytics.total_distributed)}
							</p>
							<p className='text-xs text-gray-400 mt-1'>
								{t('merchant.couponDetail.redemptionRateDesc', 'redeemed / distributed')}
							</p>
						</div>
						<div className='border rounded-lg p-4'>
							<p className='text-sm text-gray-500'>
								{t('merchant.couponDetail.viewRate', 'View Rate')}
							</p>
							<p className='text-xl font-bold text-gray-900'>
								{calculateRate(analytics.total_views, analytics.total_distributed)}
							</p>
							<p className='text-xs text-gray-400 mt-1'>
								{t('merchant.couponDetail.viewRateDesc', 'views / distributed')}
							</p>
						</div>
					</div>

					{/* Top Sharers */}
					<div>
						<h3 className='text-md font-medium text-gray-900 mb-3 flex items-center gap-2'>
							<Users className='w-4 h-4' />
							{t('merchant.couponDetail.topSharers', 'Top Sharers')}
						</h3>
						{analytics.top_sharers.length === 0 ? (
							<div className='text-center py-6 bg-gray-50 rounded-lg'>
								<p className='text-gray-500'>
									{t('merchant.couponDetail.noSharesYet', 'No shares yet')}
								</p>
							</div>
						) : (
							<div className='space-y-2'>
								{analytics.top_sharers.slice(0, 5).map((sharer, index) => (
									<div
										key={sharer.user_id}
										className='flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg'
									>
										<div className='flex items-center gap-3'>
											<span className='w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600'>
												{index + 1}
											</span>
											<span className='text-gray-900'>
												{hasText(sharer.display_name)
													? sharer.display_name
													: t('merchant.couponDetail.anonymousUser', 'Anonymous User')}
											</span>
										</div>
										<span className='text-sm font-medium text-gray-600'>
											{sharer.share_count} {t('merchant.couponDetail.shares', 'shares')}
										</span>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CouponDetail;
